/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import Slide from "@mui/material/Slide";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";

// @mui material components

// Argon Dashboard 2 PRO MUI components
import { useNavigate } from "react-router-dom";

import CustomerLineChart, {
  CustomerLineChartDashboard,
} from "0.Mine/0.Components/CRM/Customers/CustomerLineChart";
import { useHandleCalendar } from "0.Mine/1.Hooks/business/CalendarEvent";
import { UseHandleDashboard } from "0.Mine/1.Hooks/dashboard/Dashboard";
import { UseMetricool } from "0.Mine/1.Hooks/social/metricool";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { useSession } from "0.Mine/2.Context/sessionContext";
import NoBirthday from "assets/images/nobirthday.png";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 PRO MUI example components
import Calendar from "examples/Calendar";
import BackgroundBlogCard from "examples/Cards/BlogCards/BackgroundBlogCard";

// Data

// import kalVisualsSquare from "assets/images/kal-visuals-square.jpg";
import ClickableCounterCard from "examples/Cards/CounterCards/DefaultCounterCard/ClickableCounter";
import MessageCard from "examples/Cards/MessageCard";
import MessageCardDashboard from "examples/Cards/MessageCardDashboard";
import PlaceholderCard from "examples/Cards/PlaceholderCard";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ThinBarChart from "examples/Charts/BarCharts/ThinBarChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import ComplexReportsDoughnutChart from "examples/Charts/DoughnutCharts/ComplexReportsDoughnutChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import MiniGradientLineChart from "examples/Charts/LineCharts/MiniGradientLineChart";
import MixedChart from "examples/Charts/MixedChart";
import PieChart from "examples/Charts/PieChart";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import CategoriesList from "examples/Lists/CategoriesList";
import RankingsList from "examples/Lists/RankingsList";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BusinessNavbar from "examples/Navbars/DashboardNavbar/businessNavbar";
import calendarEventsData from "layouts/dashboards/crm/data/calendarEventsData";
import categoriesListData from "layouts/dashboards/crm/data/categoriesListData";
import miniGradientLineChartData from "layouts/dashboards/crm/data/miniGradientLineChartData";
import rankingsListData from "layouts/dashboards/crm/data/rankingsListData";

//Animations

const progressDoughnutChartData = {
  labels: ["Like totali", "Interazioni"],
  datasets: {
    label: "Projects",
    backgroundColors: ["info", "secondary"],
    data: [75, 25],
  },
};

export function extractLastSevenDaysMetrics(posts) {
  // Return empty array if no posts data
  if (!Array.isArray(posts) || posts.length === 0) {
    return [];
  }

  // Create a date range for the past 7 days
  const today = new Date();
  const pastDays = {};

  // Initialize each of the last 7 days with zero metrics
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i);
    const dateStr = date.toISOString().split("T")[0]; // Format as YYYY-MM-DD

    pastDays[dateStr] = {
      date: dateStr,
      impressions: 0,
      interactions: 0,
      engagement: 0,
    };
  }

  // Process each post and add metrics to the appropriate day
  posts.forEach((post) => {
    // Skip if post doesn't have required data
    if (!post.publicationDate?.dateTime || !post.metrics) return;

    // Extract date from the post's publication date
    const postDate = post.publicationDate.dateTime.split("T")[0];

    // Skip if the post is not from the last 7 days
    if (!pastDays[postDate]) return;

    // Add metrics data (handle null values)
    pastDays[postDate].impressions += post.metrics.IMPRESSIONS || 0;
    pastDays[postDate].interactions += post.metrics.INTERACTIONS || 0;
    pastDays[postDate].engagement += post.metrics.ENGAGEMENT || 0;
  });

  // Convert to sorted array
  const result = Object.values(pastDays).sort((a, b) => new Date(a.date) - new Date(b.date));

  return result;
}

function extractTotalSocialMetrics(posts) {
  // Handle invalid input
  if (!Array.isArray(posts) || posts.length === 0) {
    return {
      totalEngagement: 0,
      totalInteractions: 0,
      totalImpressions: 0,
    };
  }

  // Initialize counters
  let totalEngagement = 0;
  let totalInteractions = 0;
  let totalImpressions = 0;
  posts.forEach((post) => {
    // Skip if post doesn't have metrics or metrics are null
    if (!post.metrics) return;

    // Add metrics, handling null/undefined values
    totalInteractions += post.metrics.INTERACTIONS || 0;
    totalEngagement += post.metrics.ENGAGEMENT || 0;
    totalImpressions += post.metrics.IMPRESSIONS || 0;
  });

  return {
    totalEngagement,
    totalInteractions,
    totalImpressions,
  };
}

function generateRandomColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

// Custom hook for scroll-based animations
function useScrollAnimation(threshold = 0.1) {
  const [elements, setElements] = useState({});
  const [visibleElements, setVisibleElements] = useState({});

  useEffect(() => {
    const observers = {};

    Object.entries(elements).forEach(([id, element]) => {
      if (!element) return;

      observers[id] = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleElements((prev) => ({ ...prev, [id]: true }));
              observers[id].unobserve(entry.target);
            }
          });
        },
        { threshold, rootMargin: "0px 0px -100px 0px" }
      );

      observers[id].observe(element);
    });

    return () => {
      Object.values(observers).forEach((observer) => observer.disconnect());
    };
  }, [elements, threshold]);

  const registerElement = useCallback((id, element) => {
    if (element) {
      setElements((prev) => ({ ...prev, [id]: element }));
    }
  }, []);

  return { registerElement, isVisible: visibleElements };
}

function CRM() {
  return (
    <DashboardLayout>
      <CRMWithContext />
    </DashboardLayout>
  );
}

function CRMWithContext() {
  // Your existing states
  const { visitorsChart, incomeChart } = miniGradientLineChartData;
  const { transactionsData, revenueData } = rankingsListData;
  const navigate = useNavigate();
  const { session } = useBusinessContext();
  const [dashboardData, setDashboardData] = useState(null);
  const [socialMediaData, setSocialMediaData] = useState(null);
  const { getDashboardData } = UseHandleDashboard();
  const { getallMetricsAndPosts } = UseMetricool();
  const [socialData, setSocialData] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const { getAllCalendarEvents } = useHandleCalendar();

  const [loading, setLoading] = useState(true);
  const [animationComplete, setAnimationComplete] = useState({
    header: false,
    metrics: false,
    charts: false,
    calendar: false,
    socialData: false,
    rankings: false,
  });

  const triggerAnimations = () => {
    // Header cards animation
    setTimeout(() => setAnimationComplete((prev) => ({ ...prev, header: true })), 100);

    // Metrics animation
    setTimeout(() => setAnimationComplete((prev) => ({ ...prev, metrics: true })), 300);

    // Charts animation
    setTimeout(() => setAnimationComplete((prev) => ({ ...prev, charts: true })), 500);

    // Calendar animation
    setTimeout(() => setAnimationComplete((prev) => ({ ...prev, calendar: true })), 700);

    // Social data animation
    setTimeout(() => setAnimationComplete((prev) => ({ ...prev, socialData: true })), 900);

    // Rankings animation
    setTimeout(() => setAnimationComplete((prev) => ({ ...prev, rankings: true })), 1100);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        try {
          // Initialize arrays/objects for each data type
          let dashData = null;
          let socData = null;
          let calEvents = [];

          // Use Promise.allSettled for parallel, independent API calls
          const results = await Promise.allSettled([
            // Dashboard data fetch
            getDashboardData(session.selectedBusiness.id)
              .then((data) => {
                dashData = data;
                console.log("Dashboard data loaded successfully");
              })
              .catch((error) => {
                console.error("Error fetching dashboard data:", error);
              }),

            // Social media data fetch
            getallMetricsAndPosts(session.selectedBusiness.id)
              .then((data) => {
                socData = data;
                console.log("Social media data loaded successfully");
              })
              .catch((error) => {
                console.error("Error fetching social media data:", error);
              }),

            // Calendar events fetch
            getAllCalendarEvents(session.selectedBusiness.id)
              .then((data) => {
                if (data && Array.isArray(data)) {
                  calEvents = data.map((event) => ({
                    title: event.title,
                    start: new Date(event.start),
                    end: new Date(event.end),
                    className: generateRandomColor(),
                  }));
                  console.log("Calendar events loaded successfully", calEvents);
                }
              })
              .catch((error) => {
                console.error("Error fetching calendar events:", error);
              }),
          ]);

          // Update states with whatever data was successfully fetched
          if (dashData) {
            setDashboardData(dashData);
          }

          if (socData) {
            setSocialMediaData(socData);
            const totalMetrics = extractTotalSocialMetrics(socData);
            setSocialData(totalMetrics);
          }

          if (calEvents.length > 0) {
            setCalendarEvents(calEvents);
          }

          // Log results of each API call
          console.log(
            "API calls completed with results:",
            results.map((r, i) => {
              const apis = ["Dashboard", "Social Media", "Calendar"];
              return `${apis[i]}: ${r.status}`;
            })
          );

          setLoading(false);
          triggerAnimations();
        } catch (error) {
          console.error("Fatal error in fetchData:", error);
          setLoading(false);
          triggerAnimations();
        }
      }
    };

    fetchData();
  }, [session]);

  const socialMediaChartData = useMemo(() => {
    // Process metrics from social media data
    const metrics = extractLastSevenDaysMetrics(socialMediaData || []);

    // Format dates for display (DD/MM format)
    const labels = metrics.map((item) => {
      const date = new Date(item.date);
      return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
    });

    return {
      labels,
      datasets: [
        {
          label: "Impressioni",
          color: "info",
          data: metrics.map((item) => item.impressions),
        },
        {
          label: "Interazioni",
          color: "primary",
          data: metrics.map((item) => item.interactions),
        },
        {
          label: "Engagement",
          color: "dark",
          data: metrics.map((item) => item.engagement),
        },
      ],
    };
  }, [socialMediaData]);

  // Add our scroll animation hook
  const { registerElement, isVisible } = useScrollAnimation(0.1);

  // Create refs for each section
  const headerRef = useRef(null);
  const metricsRef = useRef(null);
  const calendarRef = useRef(null);
  const socialRef = useRef(null);
  const rankingsRef = useRef(null);

  // Register refs with the scroll animation hook
  useEffect(() => {
    registerElement("header", headerRef.current);
    registerElement("metrics", metricsRef.current);
    registerElement("calendar", calendarRef.current);
    registerElement("social", socialRef.current);
    registerElement("rankings", rankingsRef.current);
  }, [registerElement]);

  // Animation style helper
  const getAnimationStyle = (isVisible, delay = 0) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(30px)",
    transition: `opacity 0.8s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
  });

  return (
    <div>
      <BusinessNavbar />
      <ArgonBox py={3}>
        {/* Header section */}
        <div ref={headerRef} style={getAnimationStyle(isVisible.header, 0)}>
          <ArgonBox mb={3}>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6} lg={3}>
                <ClickableCounterCard
                  count={dashboardData ? dashboardData.leads : 0}
                  title="Totale Lead"
                  description="Contatti raccolti"
                  onClick={() => navigate("/crm/leads", { replace: true })}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ClickableCounterCard
                  count={dashboardData ? dashboardData.bookings : 0}
                  title="Prenotazioni totali"
                  description="Appuntamenti fissati"
                  onClick={() => navigate("/crm/bookings", { replace: true })}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ClickableCounterCard
                  count={0}
                  title="Visite al sito"
                  description="Navigazione utenti"
                  onClick={() => alert("You clicked on the card!")}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <ClickableCounterCard
                  count={0}
                  title="Messaggi ricevuti"
                  description="Mail & direct"
                  onClick={() => navigate("/crm/answer", { replace: true })}
                />
              </Grid>
            </Grid>
          </ArgonBox>
        </div>

        {/* Metrics section */}
        <div ref={metricsRef} style={getAnimationStyle(isVisible.metrics, 200)}>
          <ArgonBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={7} xl={8}>
                {/* Mini charts with Slide animation */}
                <Slide direction="right" in={animationComplete.metrics} timeout={800}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <MiniGradientLineChart
                        title={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ padding: "8px 0" }}
                          >
                            Visitatori Totali
                            <Tooltip title="Monitora l’andamento del traffico e scopri come cresce la tua visibilità.">
                              <HelpOutlineIcon
                                fontSize="small"
                                sx={{ cursor: "pointer", color: "text.secondary" }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        description={
                          <ArgonTypography variant="h5" fontWeight="bold">
                            0{" "}
                            <ArgonTypography variant="button" color="success" fontWeight="bold">
                              +0%
                            </ArgonTypography>
                          </ArgonTypography>
                        }
                        chart={visitorsChart}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MiniGradientLineChart
                        title={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ padding: "8px 0" }}
                          >
                            Ricavi da lead
                            <Tooltip title="Tieni d’occhio i guadagni. Ogni mese è un’opportunità da valorizzare.">
                              <HelpOutlineIcon
                                fontSize="small"
                                sx={{ cursor: "pointer", color: "text.secondary" }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        description={
                          <ArgonTypography variant="h5" fontWeight="bold">
                            0{" "}
                            <ArgonTypography variant="button" color="success" fontWeight="bold">
                              +0%
                            </ArgonTypography>
                          </ArgonTypography>
                        }
                        chart={incomeChart}
                      />
                    </Grid>
                  </Grid>
                </Slide>

                {/* Calendar with Grow animation */}
                <Grid item xs={12}>
                  <ArgonBox mt={3}>
                    <Grow in={animationComplete.calendar} timeout={1000}>
                      <div>
                        {useMemo(
                          () => (
                            <Calendar
                              header={{
                                title: "calendario",
                                date: new Date().toLocaleDateString("it-IT", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                }),
                              }}
                              headerToolbar={false}
                              initialView="dayGridMonth"
                              initialDate={new Date()}
                              events={calendarEvents}
                            />
                          ),
                          [calendarEvents]
                        )}
                      </div>
                    </Grow>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Right sidebar with Slide animation */}
              <Grid item xs={12} lg={5} xl={4}>
                <Slide direction="left" in={animationComplete.metrics} timeout={800}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <BackgroundBlogCard
                        image="https://qlickup.it/wp-content/uploads/2025/04/SFONDO-1.jpg"
                        title="Hai bisogno di supporto personalizzato?"
                        description={
                          <>
                            La tua Project Manager è qui per aiutarti. <br />
                            Scrivile quando vuoi per migliorare il tuo setup, chiarire dubbi o
                            proporre nuove idee.
                          </>
                        }
                        buttonText="read more"
                        action={{
                          type: "internal",
                          route: "/dashboards/crm",
                          label: "Richiedi una Project Manager",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={12}>
                      <CategoriesList title="Azioni rapide" categories={categoriesListData} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={12}>
                      <MessageCardDashboard
                        image={NoBirthday}
                        //text={getCustomerBdayMessage()}
                        text="Chiedi supporto all'AI per generare i tuoi contenuti, basterà un click per pubblicarli."
                        action={{
                          type: "chat",
                          label: "Chiedi a QlickAI",
                          color: "info",
                        }}
                      />
                    </Grid>
                  </Grid>
                </Slide>
              </Grid>
            </Grid>
          </ArgonBox>
        </div>

        {/* Social media charts section */}
        <div ref={socialRef} style={getAnimationStyle(isVisible.social, 200)}>
          <Zoom in={animationComplete.socialData} timeout={800}>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={4}>
                <DefaultLineChart
                  title={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Performance Social Media (7 giorni)
                      <Tooltip title="I dati delle ultime 24-48 ore potrebbero non essere ancora disponibili">
                        <HelpOutlineIcon
                          fontSize="small"
                          sx={{ cursor: "pointer", color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Box>
                  }
                  description={
                    <ArgonTypography variant="body2" color="text.secondary">
                      Metriche dai tuoi account social
                    </ArgonTypography>
                  }
                  chart={socialMediaChartData}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ComplexReportsDoughnutChart
                  title="Like e azioni social"
                  description="Gestisci i tuoi progetti e le tue attività"
                  chart={progressDoughnutChartData}
                  tooltip="Monitora l’andamento dei tuoi progetti e scopri come migliorare la tua produttività."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <VerticalBarChart
                  title={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Performance social
                      <Tooltip title="Metriche aggregate da tutti i post sui tuoi canali social">
                        <HelpOutlineIcon
                          fontSize="small"
                          sx={{ cursor: "pointer", color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Box>
                  }
                  description={
                    <ArgonTypography variant="body2" color="text.secondary">
                      Interazioni, impressioni ed engagement nell ultimo mese
                    </ArgonTypography>
                  }
                  chart={{
                    labels: ["Interazioni", "Engagement", "Impressioni"],
                    datasets: [
                      {
                        label: "Social Media",
                        data: [
                          socialData ? socialData.totalInteractions : 0,
                          socialData ? socialData.totalEngagement : 0,
                          socialData ? socialData.totalImpressions : 0,
                        ],
                        colors: ["info", "dark", "primary"],
                        barThickness: 6,
                      },
                    ],
                  }}
                />
              </Grid>
            </Grid>
          </Zoom>
        </div>

        {/* Rankings section */}
        <div ref={rankingsRef} style={getAnimationStyle(isVisible.rankings, 200)}>
          <Fade in={animationComplete.rankings} timeout={800}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <RankingsList
                  title="Query di ricerca frequenti"
                  date="01 - 28 Aprile 2025"
                  rankings={transactionsData}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {/*<RankingsList title="Andamento click e impressioni" date="01 - 28 Aprile 2025" rankings={revenueData} />*/}
                <CustomerLineChartDashboard />
              </Grid>
            </Grid>
          </Fade>
        </div>
      </ArgonBox>

      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Footer />
    </div>
  );
}

export default CRM;
